import { type Tag, type PatchedCustomerTagList } from '@hungryroot/rest';

import { tagGroups, tags, type TagGroups } from '@hungryroot/tags';
import { ArrowRightTurn } from '@hungryroot/ui-kit';
import { getTagDetails } from './getTagDetails';
import { type BuildSelectedMap, type TagPayloadDataBuilder } from './types';
import styles from './ControlTagSwitch.module.css';

enum TagPreference {
  Selected = 1,
  NotSelected = 0,
}

enum DislikeTagPreference {
  Selected = -1,
  NotSelected = 0,
}

export const getSwitchIcon = (tag: Tag) => {
  if (!tag) return null;
  if (tag.id === tags.OnlyLowSodium) {
    return (
      <span className={styles['only-low-sodium-icon']}>
        <ArrowRightTurn />
      </span>
    );
  }

  const imageUrl = tag.image_url || tag.thumbnail_url;
  if (!imageUrl) return null;
  return <img alt={tag.name} src={imageUrl} className={styles.icon} />;
};

export const buildDislikesData: TagPayloadDataBuilder = ({
  tag,
  customerId,
  selected,
  customerTags,
}) => {
  const payload: PatchedCustomerTagList[] = [
    {
      customer_id: customerId,
      tag: tag.id,
      preference: selected
        ? DislikeTagPreference.Selected
        : DislikeTagPreference.NotSelected,
    },
  ];

  const count = customerTags.filter(
    (obj) => obj.preference === DislikeTagPreference.Selected
  ).length;

  return { payload, count };
};

export const buildLikesData: TagPayloadDataBuilder = ({
  tag,
  customerId,
  selected,
  customerTags,
}) => {
  const buildPayload = (): PatchedCustomerTagList[] => {
    const tagPayload = {
      customer_id: customerId,
      tag: tag.id,
      preference: selected ? TagPreference.Selected : TagPreference.NotSelected,
    };

    if (tag.id === tags.LowSodium && !selected) {
      return [
        // If Low Sodium is deselected, Only Low Sodium should also be deselected
        {
          customer_id: customerId,
          tag: tags.OnlyLowSodium,
          preference: TagPreference.NotSelected,
        },
        tagPayload,
      ];
    }

    return [tagPayload];
  };

  const count = customerTags.filter(
    (obj) => obj.preference === TagPreference.Selected
  ).length;

  return { payload: buildPayload(), count };
};

export const buildDislikesSelectedMap: BuildSelectedMap = ({
  customerTags,
  queryTags,
}) => {
  const selectedMap: { [key: number]: boolean } | undefined = queryTags?.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: customerTags.some(
        (obj) =>
          obj.tag === cur.id && obj.preference === DislikeTagPreference.Selected
      ),
    }),
    {} as { [key: number]: boolean }
  );

  return selectedMap;
};

export const buildLikesSelectedMap: BuildSelectedMap = ({
  customerTags,
  queryTags,
}):
  | {
      [key: number]: boolean;
    }
  | undefined => {
  const selectedMap = queryTags?.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: customerTags.some(
        (obj) => obj.tag === cur.id && obj.preference === TagPreference.Selected
      ),
    }),
    {} as { [key: number]: boolean }
  );

  return selectedMap;
};

/** Filters out tags that may be in a tag group but should not be rendered */
export const getShouldRenderTag = (tagGroupId: TagGroups) => (tag: Tag) => {
  if (
    tag.id === tags.LowCalorie ||
    tag.id === tags.NoSugar ||
    tag.id === tags.Cals500To650
  ) {
    return false;
  }

  const { icon } = getTagDetails(tag.id);

  if (tagGroupId === tagGroups.tools && icon === undefined) {
    return false;
  }

  return true;
};

/** Checks if a tag is selected or not */
export const getIsEnabled =
  (
    selectedMap:
      | {
          [key: number]: boolean;
        }
      | undefined
  ) =>
  (tag: Tag) => {
    const key = tag.id as keyof typeof selectedMap;
    const isEnabled = selectedMap ? selectedMap[key] : false;
    return isEnabled;
  };
